// Replays, ghosts and run codes.
//
// The sim is deterministic and seeded, so a run is *completely*
// described by `{ seed, input stream }`. That one fact is worth more
// than any feature in this directory, because it buys — for free, with
// no backend — ghost racing, attract mode, verifiable times, and
// speedrunning. This module is the part that cashes it in.
//
// Three things live here:
//
//   1. **Recording and playback.** A recorder captures one `SimInput`
//      per tick; playback re-drives `step()` with the stored stream. A
//      replay is literally the unit-test harness pointed at storage.
//   2. **Run codes.** A short pasteable string that states a *claim*
//      ("this seed, this difficulty, this time"). The replay is the
//      proof; the code is the thing you put in a tweet.
//   3. **Ghosts.** Every run is stashed in `localStorage` so a later
//      attempt can race the earlier one, and so an idle machine has
//      something to demo.
//
// ## Anti-cheat, and why this is enough
//
// `verifyReplay` re-simulates the stored inputs and checks the claimed
// final tick. Forging a time therefore means producing an input stream
// that genuinely reproduces it — which is strictly more work than
// getting good at the game. There is no server, no signature, and none
// is needed: the only claim anyone can make is one they can back with a
// stream that survives re-simulation.
//
// ## Why there is a version stamp
//
// Input recordings are not save files. Change a bullet speed and the
// same inputs produce a different run — silently, with no error, just a
// replay that gradually drifts until the ghost is somewhere absurd and
// the timer disagrees with the video. That is worse than no replay at
// all, because it lies to the viewer. So every replay carries the
// engine version it was recorded on, and a mismatch **refuses to play**
// and says why.

import { NULL_HOST, type DaemonHost } from "./host";
import { clamp } from "./rng";
import { createRun, step } from "./sim";
import {
  ARENA_H,
  ARENA_W,
  EMPTY_INPUT,
  type NiceLevel,
  type RunConfig,
  type RunModifier,
  type RunStatus,
  type SignalId,
  type SimInput,
  type SimState,
  type Vec,
} from "./types";

// ── Versions ──────────────────────────────────────────────────────────

/**
 * Bump this whenever a change to the simulation could alter the outcome
 * of an existing input stream: tuning constants, pattern data, phase
 * plans, the order RNG is consumed in — anything. Old replays then
 * refuse to play instead of desyncing. It is cheap to bump and
 * expensive not to.
 */
export const ENGINE_VERSION = 4;

/** Version of the *encoding* in this file. Only bump for wire-format
 *  changes (a new channel, a different quantisation). A replay with a
 *  format version this build doesn't understand fails to decode. */
export const REPLAY_FORMAT = 1;

/** Refuse to allocate for anything longer than an hour of ticks. A
 *  corrupt header must never turn into a 4GB array. */
const MAX_TICKS = 60 * 60 * 60;
const MAX_STRING_BYTES = 4096;

// ── Quantisation ──────────────────────────────────────────────────────

/**
 * Cursor positions are stored on a lattice this many logical units
 * apart, out of a 1000-unit arena.
 *
 * This is the single knob that decides how big a replay is, and it is
 * worth being honest about the trade. Cursor deltas are where all the
 * entropy in a run lives — roughly 4 bits per axis per tick at 1-unit
 * precision, which no amount of cleverness downstream removes — so
 * halving the precision takes about a bit per axis straight off the
 * total. Measured over a realistic four-minute run:
 *
 * Measured by `tests/daemon-replay.test.ts` over a four-minute run,
 * encoded string length:
 *
 * | POS_STEP | resolution | typical play | frantic play |
 * |----------|------------|--------------|--------------|
 * | 1        | 1 unit     | 12.6KB       | 15.9KB       |
 * | 2        | 2 units    | 10.1KB       | 12.8KB       |
 * | 4        | 4 units    | 8.2KB        | 10.2KB       |
 *
 * 2 is the setting that keeps a long run inside 12KB while staying
 * mechanically invisible: the player's hitbox radius is 9 units and the
 * graze band is 34, so a 2-unit lattice is a ninth of the smallest
 * thing the sim measures, and about one CSS pixel on a typical arena.
 * Move it to 1 if a stagger is ever visible on a very large display, or
 * to 4 if replays need to be small enough to paste; nothing else in
 * this file needs to change, but every stored replay is invalidated, so
 * bump `REPLAY_FORMAT` when you do.
 *
 * A power of two on purpose: `q * POS_STEP` and `x / POS_STEP` are both
 * exact in binary floating point, so quantise → dequantise → quantise
 * is idempotent and a replay of a replay is identical.
 */
export const POS_STEP = 2;

/** Canonical order of the signal bits in the recorded bitmask. This is
 *  wire format — it is deliberately NOT derived from `SIGNALS` in
 *  sim.ts, because reordering that array for HUD reasons must not
 *  silently reinterpret every stored replay. Adding a signal means
 *  appending here and bumping `REPLAY_FORMAT`. */
const SIGNAL_BITS: SignalId[] = ["sigterm", "sigstop", "sigkill", "renice", "strace"];

/** Every value of `NiceLevel`, for validating a decoded replay. Also
 *  spelled out in `daily.ts` for validating `?nice=`; the real home is
 *  `types.ts` next to the type, which this agent doesn't own. */
const NICE_LEVELS: NiceLevel[] = [19, 10, 5, 0, -5, -10, -15, -20];
const STATUSES: RunStatus[] = ["intro", "running", "draft", "won", "lost"];

/** What kind of run this was. Dailies and free-play seeds are ranked
 *  separately; practice runs are recorded but never ranked. */
export type RunCategory = "daily" | "seeded" | "practice";
const CATEGORIES: RunCategory[] = ["daily", "seeded", "practice"];

const finite = (v: number, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

/**
 * Snap one tick of input to the exact form that gets stored.
 *
 * **The recorder hands this back and the caller must feed the returned
 * value to `step()`, not the raw one.** Recording something other than
 * what the sim consumed is the classic way to build a replay system
 * that is subtly wrong forever; returning the canonical value makes
 * that mistake impossible rather than merely documented.
 *
 * It also dedupes and orders `signals`, so the bitmask encoding is
 * lossless.
 */
export function quantizeInput(input: SimInput): SimInput {
  const qx = Math.round(clamp(finite(input.x, EMPTY_INPUT.x), 0, ARENA_W) / POS_STEP);
  const qy = Math.round(clamp(finite(input.y, EMPTY_INPUT.y), 0, ARENA_H) / POS_STEP);
  const signals: SignalId[] = [];
  const raw = Array.isArray(input.signals) ? input.signals : [];
  for (const id of SIGNAL_BITS) if (raw.includes(id)) signals.push(id);
  return { x: qx * POS_STEP, y: qy * POS_STEP, fire: !!input.fire, signals };
}

// ── Replay shape ──────────────────────────────────────────────────────

export type ReplayMeta = {
  /** Engine version this stream was recorded against. */
  engine: number;
  seed: string;
  nice: NiceLevel;
  modifier: RunModifier | null;
  daily: boolean;
  category: RunCategory;
  /** Claimed final tick — the run timer. Verified by re-simulation. */
  ticks: number;
  /** Claimed final status. Also verified. */
  status: RunStatus;
  /** Wall-clock seconds since epoch. Used only to sort stored runs;
   *  the sim never sees it and nothing about fairness depends on it. */
  recordedAt: number;
};

/**
 * A draft pick, which is the one piece of player intent that isn't a
 * `SimInput`. `step()` parks the run in `draft` and waits for the run
 * controller to apply an upgrade, so a stream alone cannot get past the
 * end of a phase. Recording the pick here keeps replays whole; playback
 * needs a `resolveDraft` callback to apply it (see `PlaybackOptions`).
 */
export type ReplayDraft = { tick: number; pick: number };

export type Replay = {
  meta: ReplayMeta;
  inputs: SimInput[];
  drafts: ReplayDraft[];
};

/** Rebuild the `RunConfig` a replay was recorded with. */
export function replayConfig(meta: ReplayMeta): RunConfig {
  return { seed: meta.seed, nice: meta.nice, modifier: meta.modifier, daily: meta.daily };
}

// ── Recorder ──────────────────────────────────────────────────────────

export type Recorder = {
  /** Ticks captured so far. */
  readonly length: number;
  /**
   * Canonicalise and append one tick of input. **Feed the return value
   * to `step()`** — it is the value that will be stored, and passing
   * anything else makes the replay disagree with the live run.
   */
  record: (input: SimInput) => SimInput;
  /** Note an upgrade pick taken at `tick`. */
  draft: (tick: number, pick: number) => void;
  /** A copy of the captured stream. */
  inputs: () => SimInput[];
  /** Close the recording against the run's final state. */
  finish: (state: SimState) => Replay;
};

export function createRecorder(
  config: RunConfig,
  opts: { category?: RunCategory; now?: number } = {},
): Recorder {
  const inputs: SimInput[] = [];
  const drafts: ReplayDraft[] = [];
  const category: RunCategory = opts.category ?? (config.daily ? "daily" : "seeded");
  const recordedAt = Math.floor((opts.now ?? Date.now()) / 1000);

  return {
    get length(): number {
      return inputs.length;
    },
    record(input: SimInput): SimInput {
      const q = quantizeInput(input);
      // An hour of ticks is not a run, it is a stuck tab. Stop growing
      // the array; verification will fail the recording, which is the
      // correct outcome for something that was never a real attempt.
      if (inputs.length < MAX_TICKS) inputs.push(q);
      return q;
    },
    draft(tick: number, pick: number): void {
      drafts.push({ tick: Math.max(0, Math.floor(tick)), pick: Math.max(0, Math.floor(pick)) });
    },
    inputs(): SimInput[] {
      return inputs.slice();
    },
    finish(state: SimState): Replay {
      return {
        meta: {
          engine: ENGINE_VERSION,
          seed: config.seed,
          nice: config.nice,
          modifier: config.modifier,
          daily: config.daily,
          category,
          ticks: state.tick,
          status: state.status,
          recordedAt,
        },
        inputs: inputs.slice(),
        drafts: drafts.slice(),
      };
    },
  };
}

// ── Playback ──────────────────────────────────────────────────────────

export type PlaybackOptions = {
  /**
   * Apply the recorded upgrade pick and return the run to `running`.
   * The sim deliberately doesn't know how to do this — it stops at the
   * draft and lets the run controller decide — so playback borrows the
   * controller's resolver. Without one, a replay that reaches a draft
   * stops there and says so rather than pretending the run ended.
   */
  resolveDraft?: (state: SimState, pick: number) => SimState;
  /** Called after every tick. For rendering a replay back at speed. */
  onTick?: (state: SimState, tick: number) => void;
  /** Stop after this many ticks. Used by the ghost pre-roll. */
  limit?: number;
};

export type PlaybackResult = {
  ok: boolean;
  state: SimState | null;
  /** Populated when `ok` is false. Always human-readable — it gets
   *  shown to whoever tried to watch the replay. */
  reason?: string;
};

/** Why this build won't play a given replay, or null if it will. */
export function replayRefusal(meta: ReplayMeta): string | null {
  if (meta.engine !== ENGINE_VERSION) {
    return `recorded on DAEMON engine v${meta.engine}; this build runs v${ENGINE_VERSION}`;
  }
  if (!NICE_LEVELS.includes(meta.nice)) return `unknown nice level ${meta.nice}`;
  return null;
}

/**
 * Re-drive the sim through a stored input stream.
 *
 * This is the same three lines as the test harness in
 * `tests/daemon-sim.test.ts`, and that is not a coincidence — if the
 * two ever diverge, one of them is wrong.
 *
 * Two contracts the live run controller has to honour or replays will
 * not match: the first tick's `prevInput` is `EMPTY_INPUT` (it feeds
 * the `lead` emitter's cursor-velocity term), and every input fed to
 * `step()` is the value `Recorder.record` handed back.
 */
export function playReplay(replay: Replay, opts: PlaybackOptions = {}): PlaybackResult {
  const refusal = replayRefusal(replay.meta);
  if (refusal) return { ok: false, state: null, reason: refusal };

  const limit = opts.limit ?? replay.inputs.length;
  const drafts = new Map(replay.drafts.map((d) => [d.tick, d.pick]));

  let s = createRun(replayConfig(replay.meta));
  let prev = EMPTY_INPUT;

  const last = Math.min(replay.inputs.length, limit) - 1;

  for (let i = 0; i <= last; i++) {
    const input = replay.inputs[i]!;
    s = step(s, input, prev);
    prev = input;

    if (s.status === "draft") {
      const pick = drafts.get(s.tick);
      if (pick !== undefined && opts.resolveDraft) {
        s = opts.resolveDraft(s, pick);
      } else if (i < last) {
        // A draft mid-stream with nothing to apply would stall the sim
        // and quietly hand back a "finished" run at the wrong tick.
        // Recording ending *on* a draft is fine — that is a run that
        // was paused, not one we failed to reproduce.
        return {
          ok: false,
          state: s,
          reason: `replay reached a draft at tick ${s.tick} with no recorded pick to apply`,
        };
      }
    }

    opts.onTick?.(s, i + 1);
  }

  return { ok: true, state: s };
}

export type Verification = {
  ok: boolean;
  finalTick: number;
  status: RunStatus;
  /** Populated when `ok` is false. */
  reason?: string;
};

/**
 * Re-simulate a replay and confirm it produces the time it claims.
 *
 * This is the entire anti-cheat story, and it is sufficient. A forged
 * time requires an input stream that actually reproduces it.
 */
export function verifyReplay(replay: Replay, opts: PlaybackOptions = {}): Verification {
  const played = playReplay(replay, opts);
  if (!played.ok || !played.state) {
    return {
      ok: false,
      finalTick: played.state?.tick ?? 0,
      status: played.state?.status ?? "intro",
      reason: played.reason ?? "replay would not play",
    };
  }
  const s = played.state;
  if (s.tick !== replay.meta.ticks) {
    return {
      ok: false,
      finalTick: s.tick,
      status: s.status,
      reason: `claimed ${replay.meta.ticks} ticks, re-simulation ended at ${s.tick}`,
    };
  }
  if (s.status !== replay.meta.status) {
    return {
      ok: false,
      finalTick: s.tick,
      status: s.status,
      reason: `claimed "${replay.meta.status}", re-simulation ended "${s.status}"`,
    };
  }
  return { ok: true, finalTick: s.tick, status: s.status };
}

// ── Byte plumbing ─────────────────────────────────────────────────────
//
// Hand-rolled rather than JSON because `JSON.stringify` of 14,400 input
// objects is ~617KB (measured), and no amount of deflate rescues a
// format that spends 43 bytes a tick on punctuation. The packed form is
// 31KB and deflates to ~7.5KB.

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

class ByteWriter {
  private buf = new Uint8Array(4096);
  private len = 0;

  private need(n: number): void {
    if (this.len + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.len + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
  }

  u8(v: number): void {
    this.need(1);
    this.buf[this.len++] = v & 0xff;
  }

  /** LEB128, unsigned. Values are all well under 2^32 here. */
  varint(v: number): void {
    let n = v >>> 0;
    this.need(5);
    while (n >= 0x80) {
      this.buf[this.len++] = (n & 0x7f) | 0x80;
      n >>>= 7;
    }
    this.buf[this.len++] = n;
  }

  /** Zigzag, so small negatives cost one byte like small positives. */
  zigzag(v: number): void {
    this.varint(((v << 1) ^ (v >> 31)) >>> 0);
  }

  u32(v: number): void {
    this.need(4);
    this.buf[this.len++] = v & 0xff;
    this.buf[this.len++] = (v >>> 8) & 0xff;
    this.buf[this.len++] = (v >>> 16) & 0xff;
    this.buf[this.len++] = (v >>> 24) & 0xff;
  }

  str(s: string): void {
    const bytes = TEXT_ENCODER.encode(s);
    this.varint(bytes.length);
    this.need(bytes.length);
    this.buf.set(bytes, this.len);
    this.len += bytes.length;
  }

  bytes(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}

class ByteReader {
  private pos = 0;
  constructor(private readonly buf: Uint8Array) {}

  private check(n: number): void {
    if (this.pos + n > this.buf.length) throw new RangeError("replay: truncated payload");
  }

  u8(): number {
    this.check(1);
    return this.buf[this.pos++]!;
  }

  varint(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      this.check(1);
      const b = this.buf[this.pos++]!;
      result |= (b & 0x7f) << shift;
      if ((b & 0x80) === 0) break;
      shift += 7;
      if (shift > 35) throw new RangeError("replay: varint overflow");
    }
    return result >>> 0;
  }

  zigzag(): number {
    const n = this.varint();
    return (n >>> 1) ^ -(n & 1);
  }

  u32(): number {
    this.check(4);
    const v =
      this.buf[this.pos]! |
      (this.buf[this.pos + 1]! << 8) |
      (this.buf[this.pos + 2]! << 16) |
      (this.buf[this.pos + 3]! << 24);
    this.pos += 4;
    return v >>> 0;
  }

  str(): string {
    const n = this.varint();
    if (n > MAX_STRING_BYTES) throw new RangeError("replay: absurd string length");
    this.check(n);
    const s = TEXT_DECODER.decode(this.buf.subarray(this.pos, this.pos + n));
    this.pos += n;
    return s;
  }
}

// ── Channel encoding ──────────────────────────────────────────────────
//
// Cursor: predicted, then the residual is zigzag varint'd, with the X
// and Y streams written as separate contiguous blocks so deflate sees
// one coherent symbol distribution at a time instead of two interleaved
// ones. Interleaving costs about 20% — measured.
//
// Fire and signals: run-length and gap encoded. Both channels are empty
// on the overwhelming majority of ticks, and paying a byte per tick for
// "still nothing" is exactly the kind of waste that turns a 9KB replay
// into a 30KB one.

/**
 * Predict this tick's delta from the last one.
 *
 * Plain delta encoding (predict zero) assumes the cursor teleports;
 * full velocity extrapolation (predict the whole previous delta)
 * assumes it never changes speed. A mouse does neither, and three
 * quarters is where the residuals actually got smallest on real-shaped
 * paths — about 10% off the encoded size versus plain delta, for one
 * multiply. It has to stay integer-exact and identical on both sides,
 * which is why it is written once, here.
 */
const predict = (prevDelta: number): number => Math.round((prevDelta * 3) / 4);

function writeInputs(w: ByteWriter, inputs: SimInput[]): void {
  w.varint(inputs.length);

  let px = 0;
  let pdx = 0;
  for (const i of inputs) {
    const q = Math.round(i.x / POS_STEP);
    const d = q - px;
    w.zigzag(d - predict(pdx));
    pdx = d;
    px = q;
  }
  let py = 0;
  let pdy = 0;
  for (const i of inputs) {
    const q = Math.round(i.y / POS_STEP);
    const d = q - py;
    w.zigzag(d - predict(pdy));
    pdy = d;
    py = q;
  }

  // Fire: alternating run lengths starting with a run of `false`. The
  // first run is allowed to be zero-length when tick 0 already fires.
  const runs: number[] = [];
  let want = false;
  let run = 0;
  for (const i of inputs) {
    if (i.fire === want) {
      run++;
    } else {
      runs.push(run);
      want = !want;
      run = 1;
    }
  }
  if (inputs.length > 0) runs.push(run);
  w.varint(runs.length);
  for (const r of runs) w.varint(r);

  // Signals: sparse, so store (gap since last event, bitmask) pairs.
  const events: Array<[number, number]> = [];
  let last = -1;
  for (let t = 0; t < inputs.length; t++) {
    const sigs = inputs[t]!.signals;
    if (sigs.length === 0) continue;
    let mask = 0;
    for (const id of sigs) {
      const bit = SIGNAL_BITS.indexOf(id);
      if (bit >= 0) mask |= 1 << bit;
    }
    if (mask === 0) continue;
    events.push([t - last - 1, mask]);
    last = t;
  }
  w.varint(events.length);
  for (const [gap, mask] of events) {
    w.varint(gap);
    w.u8(mask);
  }
}

function readInputs(r: ByteReader): SimInput[] {
  const n = r.varint();
  if (n > MAX_TICKS) throw new RangeError("replay: implausible tick count");

  const xs = new Int32Array(n);
  let px = 0;
  let pdx = 0;
  for (let i = 0; i < n; i++) {
    const d = r.zigzag() + predict(pdx);
    px += d;
    pdx = d;
    xs[i] = px;
  }
  const ys = new Int32Array(n);
  let py = 0;
  let pdy = 0;
  for (let i = 0; i < n; i++) {
    const d = r.zigzag() + predict(pdy);
    py += d;
    pdy = d;
    ys[i] = py;
  }

  const fire = new Uint8Array(n);
  const runCount = r.varint();
  if (runCount > n + 2) throw new RangeError("replay: implausible run count");
  let at = 0;
  let value = 0;
  for (let i = 0; i < runCount; i++) {
    const len = r.varint();
    if (value === 1) fire.fill(1, at, Math.min(n, at + len));
    at += len;
    value ^= 1;
  }

  const inputs: SimInput[] = new Array(n);
  for (let i = 0; i < n; i++) {
    inputs[i] = {
      x: xs[i]! * POS_STEP,
      y: ys[i]! * POS_STEP,
      fire: fire[i] === 1,
      signals: [],
    };
  }

  const eventCount = r.varint();
  if (eventCount > n + 1) throw new RangeError("replay: implausible signal count");
  let t = -1;
  for (let i = 0; i < eventCount; i++) {
    t += r.varint() + 1;
    const mask = r.u8();
    if (t < 0 || t >= n) throw new RangeError("replay: signal outside the recording");
    const signals: SignalId[] = [];
    for (let bit = 0; bit < SIGNAL_BITS.length; bit++) {
      if (mask & (1 << bit)) signals.push(SIGNAL_BITS[bit]!);
    }
    inputs[t]!.signals = signals;
  }

  return inputs;
}

// ── Packing ───────────────────────────────────────────────────────────

/**
 * The uncompressed wire form. Exported because it is the honest unit
 * for size assertions, and because the sync path (localStorage under a
 * browser with no `CompressionStream`) needs it.
 *
 * Assumes the inputs came from `Recorder.record` / `quantizeInput`: a
 * hand-built stream off the lattice is silently snapped onto it, and
 * will not round-trip identically.
 */
export function packReplay(replay: Replay): Uint8Array {
  const w = new ByteWriter();
  const m = replay.meta;
  w.varint(m.engine);
  w.str(m.seed);
  w.zigzag(m.nice);
  w.str(m.modifier ? JSON.stringify(m.modifier) : "");
  w.u8(m.daily ? 1 : 0);
  w.u8(Math.max(0, CATEGORIES.indexOf(m.category)));
  w.varint(Math.max(0, Math.floor(m.ticks)));
  w.u8(Math.max(0, STATUSES.indexOf(m.status)));
  w.varint(Math.max(0, Math.floor(m.recordedAt)));

  w.varint(replay.drafts.length);
  let lastDraft = 0;
  for (const d of replay.drafts) {
    w.varint(Math.max(0, d.tick - lastDraft));
    w.varint(d.pick);
    lastDraft = d.tick;
  }

  writeInputs(w, replay.inputs);
  return w.bytes();
}

/** Inverse of `packReplay`. Returns null on anything malformed rather
 *  than throwing — a corrupt payload is a bad string someone pasted,
 *  not an exception the UI should have to catch. */
export function unpackReplay(bytes: Uint8Array): Replay | null {
  try {
    const r = new ByteReader(bytes);
    const engine = r.varint();
    const seed = r.str();
    const nice = r.zigzag() as NiceLevel;
    const modifierJson = r.str();
    const daily = r.u8() === 1;
    const category = CATEGORIES[r.u8()] ?? "seeded";
    const ticks = r.varint();
    const status = STATUSES[r.u8()] ?? "lost";
    const recordedAt = r.varint();

    let modifier: RunModifier | null = null;
    if (modifierJson) {
      const parsed = JSON.parse(modifierJson) as RunModifier;
      if (parsed && typeof parsed.id === "string") modifier = parsed;
    }

    const draftCount = r.varint();
    if (draftCount > MAX_TICKS) throw new RangeError("replay: implausible draft count");
    const drafts: ReplayDraft[] = [];
    let lastDraft = 0;
    for (let i = 0; i < draftCount; i++) {
      lastDraft += r.varint();
      drafts.push({ tick: lastDraft, pick: r.varint() });
    }

    const inputs = readInputs(r);
    if (ticks > MAX_TICKS) throw new RangeError("replay: implausible tick claim");

    return {
      meta: { engine, seed, nice, modifier, daily, category, ticks, status, recordedAt },
      inputs,
      drafts,
    };
  } catch {
    return null;
  }
}

// ── Compression ───────────────────────────────────────────────────────

const MAGIC_0 = 0x44; // 'D'
const MAGIC_1 = 0x52; // 'R'
const FLAG_DEFLATED = 1;

function hasStreams(): boolean {
  const g = globalThis as unknown as Record<string, unknown>;
  return typeof g.CompressionStream === "function" && typeof g.ReadableStream === "function";
}

async function pump(bytes: Uint8Array, mode: "deflate-raw" | "inflate-raw"): Promise<Uint8Array> {
  const g = globalThis as unknown as {
    CompressionStream: new (f: string) => TransformStream<Uint8Array, Uint8Array>;
    DecompressionStream: new (f: string) => TransformStream<Uint8Array, Uint8Array>;
    ReadableStream: typeof ReadableStream;
  };
  const transform =
    mode === "deflate-raw"
      ? new g.CompressionStream("deflate-raw")
      : new g.DecompressionStream("deflate-raw");

  const source = new g.ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });

  const reader = source.pipeThrough(transform).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.length;
    }
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

// ── base64url ─────────────────────────────────────────────────────────
//
// Hand-rolled rather than btoa/atob: those want a binary *string*, which
// means a 30KB intermediate and a `String.fromCharCode.apply` that blows
// the argument limit on a long run. This is a dozen lines and has no
// size cliff.

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const B64_INDEX: Record<string, number> = {};
for (let i = 0; i < B64.length; i++) B64_INDEX[B64[i]!] = i;

export function toBase64url(bytes: Uint8Array): string {
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out += B64[(n >>> 18) & 63]! + B64[(n >>> 12) & 63]! + B64[(n >>> 6) & 63]! + B64[n & 63]!;
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = bytes[i]! << 16;
    out += B64[(n >>> 18) & 63]! + B64[(n >>> 12) & 63]!;
  } else if (rest === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out += B64[(n >>> 18) & 63]! + B64[(n >>> 12) & 63]! + B64[(n >>> 6) & 63]!;
  }
  return out;
}

export function fromBase64url(text: string): Uint8Array | null {
  const s = text.trim().replace(/=+$/, "");
  const full = s.length >> 2;
  const rest = s.length & 3;
  if (rest === 1) return null; // impossible length
  const out = new Uint8Array(full * 3 + (rest === 2 ? 1 : rest === 3 ? 2 : 0));
  let o = 0;
  let i = 0;
  for (; i + 3 < s.length; i += 4) {
    const a = B64_INDEX[s[i]!];
    const b = B64_INDEX[s[i + 1]!];
    const c = B64_INDEX[s[i + 2]!];
    const d = B64_INDEX[s[i + 3]!];
    if (a === undefined || b === undefined || c === undefined || d === undefined) return null;
    const n = (a << 18) | (b << 12) | (c << 6) | d;
    out[o++] = (n >>> 16) & 255;
    out[o++] = (n >>> 8) & 255;
    out[o++] = n & 255;
  }
  if (rest >= 2) {
    const a = B64_INDEX[s[i]!];
    const b = B64_INDEX[s[i + 1]!];
    if (a === undefined || b === undefined) return null;
    out[o++] = ((a << 2) | (b >>> 4)) & 255;
    if (rest === 3) {
      const c = B64_INDEX[s[i + 2]!];
      if (c === undefined) return null;
      out[o++] = ((b << 4) | (c >>> 2)) & 255;
    }
  }
  return out;
}

// ── Encode / decode ───────────────────────────────────────────────────

function container(payload: Uint8Array, deflated: boolean): string {
  const out = new Uint8Array(payload.length + 4);
  out[0] = MAGIC_0;
  out[1] = MAGIC_1;
  out[2] = REPLAY_FORMAT;
  out[3] = deflated ? FLAG_DEFLATED : 0;
  out.set(payload, 4);
  return toBase64url(out);
}

/**
 * Encode a replay to a single pasteable string.
 *
 * Deflate is feature-detected, not assumed: `CompressionStream` is
 * everywhere current but the game must still work in a browser without
 * it, so the fallback is the same payload uncompressed with the flag
 * cleared. Decoding handles both, which means a replay encoded on an
 * old browser plays fine on a new one and vice versa.
 */
export async function encodeReplay(replay: Replay): Promise<string> {
  const raw = packReplay(replay);
  if (hasStreams()) {
    try {
      const squashed = await pump(raw, "deflate-raw");
      if (squashed.length < raw.length) return container(squashed, true);
    } catch {
      /* fall through to the uncompressed form */
    }
  }
  return container(raw, false);
}

/** Synchronous encode, always uncompressed. For the rare caller that
 *  cannot await (and for measuring the pre-deflate size). */
export function encodeReplaySync(replay: Replay): string {
  return container(packReplay(replay), false);
}

/** Decode a replay string. Returns null for anything that isn't one —
 *  wrong magic, wrong format version, bad base64, truncated body,
 *  garbage after inflate. Never throws. */
export async function decodeReplay(text: string): Promise<Replay | null> {
  const bytes = fromBase64url(text ?? "");
  if (!bytes || bytes.length < 5) return null;
  if (bytes[0] !== MAGIC_0 || bytes[1] !== MAGIC_1) return null;
  if (bytes[2] !== REPLAY_FORMAT) return null;

  const body = bytes.subarray(4);
  if ((bytes[3]! & FLAG_DEFLATED) === 0) return unpackReplay(body);

  const g = globalThis as unknown as Record<string, unknown>;
  if (typeof g.DecompressionStream !== "function" || typeof g.ReadableStream !== "function") {
    return null;
  }
  try {
    return unpackReplay(await pump(body, "inflate-raw"));
  } catch {
    return null;
  }
}

// ── Run codes ─────────────────────────────────────────────────────────
//
// The *claim*, not the proof. Short enough to drop in a tweet or a
// GitHub issue; anyone can decode it, load the seed, and go and beat
// the time. The full input stream is a separate, much larger artefact —
// you only need it when someone wants the claim verified.
//
// Format:  DR1-<crockford base32 of>
//   u8   code version
//   str  seed
//   zig  nice
//   u8   category
//   var  ticks
//   u32  input hash
//   u16  checksum of everything above
//
// Crockford base32 rather than base64: it is case-insensitive, has no
// visually ambiguous characters, and survives being read aloud, which
// base64url does not. ~34 characters for a typical seed.

const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const B32_INDEX: Record<string, number> = {};
for (let i = 0; i < B32.length; i++) B32_INDEX[B32[i]!] = i;
// Crockford's forgiving aliases.
B32_INDEX["I"] = 1;
B32_INDEX["L"] = 1;
B32_INDEX["O"] = 0;
B32_INDEX["U"] = B32_INDEX["V"]!;

function toBase32(bytes: Uint8Array): string {
  let out = "";
  let bits = 0;
  let acc = 0;
  for (const b of bytes) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += B32[(acc >>> bits) & 31]!;
    }
  }
  if (bits > 0) out += B32[(acc << (5 - bits)) & 31]!;
  return out;
}

function fromBase32(text: string): Uint8Array | null {
  const s = text.toUpperCase().replace(/[\s-]/g, "");
  const out = new Uint8Array(Math.floor((s.length * 5) / 8));
  let bits = 0;
  let acc = 0;
  let o = 0;
  for (const ch of s) {
    const v = B32_INDEX[ch];
    if (v === undefined) return null;
    acc = (acc << 5) | v;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >>> bits) & 0xff;
    }
  }
  // The trailing partial character must be zero-padded. Without this
  // check the low bits of the last character are simply ignored, and a
  // run code has sixteen spellings that all decode identically — which
  // makes "tampering is rejected" quietly false for the last character.
  if (bits > 0 && (acc & ((1 << bits) - 1)) !== 0) return null;
  return out.subarray(0, o);
}

/** FNV-1a, 32-bit. Same function the daily challenge uses — small, and
 *  stable across engines, which is the only property that matters. */
export function fnv1a(bytes: Uint8Array): number {
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A fingerprint of the input stream, so a run code can be tied to the
 *  replay that backs it. Canonical: it hashes the packed channels, not
 *  the JS objects, so it is identical before and after a round trip. */
export function hashInputs(inputs: SimInput[]): number {
  const w = new ByteWriter();
  writeInputs(w, inputs);
  return fnv1a(w.bytes());
}

export type RunCode = {
  seed: string;
  nice: number;
  category: RunCategory;
  ticks: number;
  inputHash: number;
};

const RUN_CODE_VERSION = 1;
const RUN_CODE_PREFIX = "DR1-";

export function encodeRunCode(code: RunCode): string {
  const w = new ByteWriter();
  w.u8(RUN_CODE_VERSION);
  w.str(code.seed);
  w.zigzag(code.nice);
  w.u8(Math.max(0, CATEGORIES.indexOf(code.category)));
  w.varint(Math.max(0, Math.floor(code.ticks)));
  w.u32(code.inputHash >>> 0);
  const body = w.bytes();
  const sum = fnv1a(body) & 0xffff;
  const full = new Uint8Array(body.length + 2);
  full.set(body);
  full[body.length] = sum & 0xff;
  full[body.length + 1] = (sum >>> 8) & 0xff;
  return RUN_CODE_PREFIX + toBase32(full);
}

/**
 * Decode a run code, or null if it is not one.
 *
 * The checksum catches typos and casual edits — it is emphatically NOT
 * the anti-cheat, because anyone can recompute it. Tampering is caught
 * for real by `checkRunCode`, which demands a replay whose inputs hash
 * to the claimed value and whose re-simulation lands on the claimed
 * tick.
 */
export function decodeRunCode(text: string): RunCode | null {
  const raw = (text ?? "").trim();
  const stripped = raw.toUpperCase().startsWith(RUN_CODE_PREFIX)
    ? raw.slice(RUN_CODE_PREFIX.length)
    : raw;
  const bytes = fromBase32(stripped);
  if (!bytes || bytes.length < 10) return null;

  const body = bytes.subarray(0, bytes.length - 2);
  const want = bytes[bytes.length - 2]! | (bytes[bytes.length - 1]! << 8);
  if ((fnv1a(body) & 0xffff) !== want) return null;

  try {
    const r = new ByteReader(body);
    if (r.u8() !== RUN_CODE_VERSION) return null;
    const seed = r.str();
    const nice = r.zigzag();
    const category = CATEGORIES[r.u8()] ?? "seeded";
    const ticks = r.varint();
    const inputHash = r.u32();
    if (ticks > MAX_TICKS) return null;
    return { seed, nice, category, ticks, inputHash };
  } catch {
    return null;
  }
}

/** The run code for a finished replay. */
export function runCodeFor(replay: Replay): string {
  return encodeRunCode({
    seed: replay.meta.seed,
    nice: replay.meta.nice,
    category: replay.meta.category,
    ticks: replay.meta.ticks,
    inputHash: hashInputs(replay.inputs),
  });
}

/**
 * Does this replay actually back this claim? Checks the code parses,
 * describes the same run, fingerprints the same inputs, and then
 * re-simulates. All four have to hold.
 */
export function checkRunCode(
  text: string,
  replay: Replay,
  opts: PlaybackOptions = {},
): { ok: boolean; reason?: string } {
  const code = decodeRunCode(text);
  if (!code) return { ok: false, reason: "not a valid run code" };
  if (code.seed !== replay.meta.seed) return { ok: false, reason: "run code is for another seed" };
  if (code.nice !== replay.meta.nice) return { ok: false, reason: "run code claims a different difficulty" };
  if (code.category !== replay.meta.category) return { ok: false, reason: "run code claims a different category" };
  if (code.inputHash !== hashInputs(replay.inputs)) {
    return { ok: false, reason: "run code does not match this input stream" };
  }
  const verified = verifyReplay(replay, opts);
  if (!verified.ok) return { ok: false, reason: verified.reason };
  if (verified.finalTick !== code.ticks) {
    return { ok: false, reason: `run code claims ${code.ticks} ticks, replay ends at ${verified.finalTick}` };
  }
  return { ok: true };
}

// ── Ghosts and storage ────────────────────────────────────────────────

export const REPLAY_STORE_SUFFIX = "replays.v1";

/** How many runs to keep. Deliberately small: localStorage is a shared
 *  5MB budget for the whole of skenOS, and DAEMON is a guest in it. */
export const MAX_STORED_RUNS = 8;

/**
 * Hard ceiling on the encoded *characters* we will keep across all
 * runs. Browsers store localStorage values as UTF-16, so this is about
 * 256KB of real quota — a twentieth of the usual 5MB budget, which is
 * a fair share for a game living inside a portfolio site.
 */
export const MAX_STORE_CHARS = 128 * 1024;

export type StoredRun = {
  /** The claim, so a stored run can be shared without re-encoding. */
  code: string;
  meta: ReplayMeta;
  /** The encoded replay — the proof, and the ghost. */
  data: string;
};

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Sandboxed iframes throw on *access*, not just on use.
    return null;
  }
}

const storeKey = (host: DaemonHost): string => host.storageKey(REPLAY_STORE_SUFFIX);

/**
 * Is `a` a better run than `b`? A win beats a loss; among wins, fewer
 * ticks; among losses, *more* ticks, because the only thing a failed
 * run can be proud of is how long it lasted.
 */
export function isBetterRun(a: ReplayMeta, b: ReplayMeta): boolean {
  const aWon = a.status === "won";
  const bWon = b.status === "won";
  if (aWon !== bWon) return aWon;
  return aWon ? a.ticks < b.ticks : a.ticks > b.ticks;
}

const groupKey = (m: ReplayMeta): string => `${m.category}|${m.seed}|${m.nice}`;

export function loadRuns(host: DaemonHost = NULL_HOST, storage?: Storage | null): StoredRun[] {
  const s = storage === undefined ? safeStorage() : storage;
  if (!s) return [];
  try {
    const raw = s.getItem(storeKey(host));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is StoredRun =>
        !!r &&
        typeof (r as StoredRun).data === "string" &&
        typeof (r as StoredRun).code === "string" &&
        !!(r as StoredRun).meta &&
        typeof (r as StoredRun).meta.seed === "string",
    );
  } catch {
    // Corrupt payload — behave as if there were no history rather than
    // taking the game down with it.
    return [];
  }
}

/** Slots reserved for recent runs regardless of how good they are.
 *  Without this, a shelf of ancient personal bests fills the store and
 *  the attract loop replays the same clears forever. */
const RECENT_SLOTS = 2;

/**
 * Trim a run list to the cap.
 *
 * The best run for each `(category, seed, nice)` is a candidate to keep
 * forever — clears first, then most recent — and the last couple of
 * slots always go to whatever happened most recently. Keeping *every*
 * run is what makes attract mode possible; keeping the best is what
 * makes ghosts worth racing; keeping some of both is what stops the two
 * from starving each other.
 */
export function trimRuns(runs: StoredRun[]): StoredRun[] {
  const best = new Map<string, StoredRun>();
  for (const r of runs) {
    const k = groupKey(r.meta);
    const cur = best.get(k);
    if (!cur || isBetterRun(r.meta, cur.meta)) best.set(k, r);
  }

  const keep = Array.from(best.values()).sort((a, b) => {
    const aWon = a.meta.status === "won" ? 1 : 0;
    const bWon = b.meta.status === "won" ? 1 : 0;
    if (aWon !== bWon) return bWon - aWon;
    return b.meta.recordedAt - a.meta.recordedAt;
  });

  const seen = new Set(keep);
  const rest = runs
    .filter((r) => !seen.has(r))
    .sort((a, b) => b.meta.recordedAt - a.meta.recordedAt);

  const primary = keep.slice(0, Math.max(1, MAX_STORED_RUNS - RECENT_SLOTS));
  const chosen = new Set(primary);
  // Recent runs get the remaining slots ahead of the *surplus* bests —
  // that is what RECENT_SLOTS is reserving.
  const ordered = primary.concat(
    rest.concat(keep.slice(primary.length)).filter((r) => !chosen.has(r)),
  );

  const out: StoredRun[] = [];
  let chars = 0;
  for (const r of ordered) {
    if (out.length >= MAX_STORED_RUNS) break;
    if (chars + r.data.length > MAX_STORE_CHARS) continue;
    chars += r.data.length;
    out.push(r);
  }
  return out.sort((a, b) => b.meta.recordedAt - a.meta.recordedAt);
}

/**
 * Persist a run. **Every** run, not only personal bests — a machine
 * with a history of ordinary runs has something to play back when it's
 * left alone, and an attract loop of nothing but flawless clears would
 * be both dull and a lie.
 */
export function saveRun(
  host: DaemonHost,
  run: StoredRun,
  storage?: Storage | null,
): StoredRun[] {
  const s = storage === undefined ? safeStorage() : storage;
  const next = trimRuns([run, ...loadRuns(host, s)]);
  if (!s) return next;
  try {
    s.setItem(storeKey(host), JSON.stringify(next));
  } catch {
    // Quota, private mode, or a full disk. Try once more with only the
    // best run — losing history is fine, losing the PB is not.
    try {
      const only = next.slice(0, 1);
      s.setItem(storeKey(host), JSON.stringify(only));
      return only;
    } catch {
      /* give up quietly; the run still happened */
    }
  }
  return next;
}

/** Encode and persist in one step. */
export async function recordRun(
  host: DaemonHost,
  replay: Replay,
  storage?: Storage | null,
): Promise<StoredRun[]> {
  const data = await encodeReplay(replay);
  return saveRun(host, { code: runCodeFor(replay), meta: replay.meta, data }, storage);
}

/**
 * Which stored runs are comparable. `nice` is part of the key because
 * racing a ghost recorded at a kinder difficulty is not a race — the
 * fight itself was different — so leaving it out silently produces
 * flattering ghosts. Omit it only when you genuinely want the best run
 * on a seed at any difficulty.
 */
export type RunKey = { category: RunCategory; seed: string; nice?: NiceLevel };

const matches = (m: ReplayMeta, key: RunKey): boolean =>
  m.category === key.category &&
  m.seed === key.seed &&
  (key.nice === undefined || m.nice === key.nice);

/** The best stored run for a seed, or null if there isn't one. */
export function bestRun(
  host: DaemonHost,
  key: RunKey,
  storage?: Storage | null,
): StoredRun | null {
  let best: StoredRun | null = null;
  for (const r of loadRuns(host, storage)) {
    if (!matches(r.meta, key)) continue;
    if (!best || isBetterRun(r.meta, best.meta)) best = r;
  }
  return best;
}

/** Load the ghost to race on this seed. Null when there is no history,
 *  or when what history there is was recorded on an older engine. */
export async function loadGhost(
  host: DaemonHost,
  key: RunKey,
  storage?: Storage | null,
): Promise<GhostPlayer | null> {
  const best = bestRun(host, key, storage);
  if (!best) return null;
  const replay = await decodeReplay(best.data);
  if (!replay || replayRefusal(replay.meta)) return null;
  return createGhostPlayer(replay);
}

/** Pick a run for the attract loop. `n` indexes the list rather than
 *  being random, so the caller decides the cadence and the choice stays
 *  deterministic for tests. */
export function pickAttractRun(runs: StoredRun[], n: number): StoredRun | null {
  if (runs.length === 0) return null;
  const i = ((Math.floor(n) % runs.length) + runs.length) % runs.length;
  return runs[i]!;
}

// ── Ghost playback ────────────────────────────────────────────────────

export type GhostPlayer = {
  meta: ReplayMeta;
  /** Ticks in the recording. */
  length: number;
  /**
   * The ghost's cursor at `tick`, or null once the recording runs out —
   * a ghost that freezes at its death position and hangs around is a
   * distraction, so the renderer is told to stop drawing it.
   *
   * `alpha` interpolates toward the next tick, for renderers running
   * ahead of the 60Hz sim.
   */
  at: (tick: number, alpha?: number) => Vec | null;
  /** The full input at `tick`, for a ghost that also shows its signals. */
  input: (tick: number) => SimInput | null;
  finished: (tick: number) => boolean;
};

/**
 * A ghost is just the cursor channel of a replay, read positionally.
 * There is no second simulation: racing a ghost costs one array index
 * per frame, which is why it can be on by default.
 */
export function createGhostPlayer(replay: Replay): GhostPlayer {
  const inputs = replay.inputs;
  const n = inputs.length;
  return {
    meta: replay.meta,
    length: n,
    at(tick: number, alpha = 0): Vec | null {
      const i = Math.floor(tick);
      if (i < 0 || i >= n) return null;
      const a = inputs[i]!;
      if (alpha <= 0 || i + 1 >= n) return { x: a.x, y: a.y };
      const b = inputs[i + 1]!;
      const t = clamp(alpha, 0, 1);
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    },
    input(tick: number): SimInput | null {
      const i = Math.floor(tick);
      return i >= 0 && i < n ? inputs[i]! : null;
    },
    finished(tick: number): boolean {
      return Math.floor(tick) >= n;
    },
  };
}
